import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  useColorModeValue,
  IconButton,
  Divider,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import NotificationItem from './NotificationItem';
import type { Notification } from '../../types/notification';
import { NOTIFICATION_UI_CONFIG } from '../../constants/notifications';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  loading: boolean;
  onNotificationClick: (notification: Notification, fromPanel?: boolean) => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  notifications,
  loading,
  onNotificationClick,
}) => {
  // Always call hooks at the top level, never inside conditions
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const shadowColor = useColorModeValue('lg', 'dark-lg');

  // Early return if not open
  if (!isOpen) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      right={0}
      h="100vh"
      w={{ base: NOTIFICATION_UI_CONFIG.PANEL.WIDTH.MOBILE, md: NOTIFICATION_UI_CONFIG.PANEL.WIDTH.DESKTOP }}
      bg={bgColor}
      borderLeft="1px"
      borderColor={borderColor}
      boxShadow={shadowColor}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          zIndex={1000}
          transform={isOpen ? "translateX(0)" : "translateX(100%)"}
          transition="transform 0.3s ease-in-out"
        >
        {/* Header */}
        <HStack
          justify="space-between"
          align="center"
          p={4}
          borderBottom="1px"
          borderColor={borderColor}
        >
          <Text fontSize="lg" fontWeight="bold">
            Notifications
          </Text>
          <IconButton
            aria-label="Close notifications"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={onClose}
          />
        </HStack>

        {/* Content */}
        <Box flex={1} overflow="hidden" display="flex" flexDirection="column">
          {loading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              h="200px"
              gap={3}
            >
              <Spinner size="lg" />
              <Text color="gray.500">Loading notifications...</Text>
            </Box>
          ) : notifications.length === 0 ? (
            <Box p={4}>
              <Alert status="info">
                <AlertIcon />
                No notifications found.
              </Alert>
            </Box>
          ) : (
            <VStack
              spacing={0}
              align="stretch"
              overflowY="auto"
              flex={1}
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: useColorModeValue('gray.300', 'gray.600'),
                  borderRadius: '24px',
                },
              }}
            >
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onClick={() => onNotificationClick(notification, true)}
                  />
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </VStack>
          )}
        </Box>

        {/* Footer */}
        {/* {notifications.length > 5 && (
          <Box p={4} borderTop="1px" borderColor={borderColor}>
            <Button
              variant="outline"
              size="sm"
              width="full"
              onClick={() => {
                window.location.href = '/notifications';
                onClose();
              }}
            >
              View All Notifications
            </Button>
          </Box>
        )} */}
      </Box>
  );
};

export default NotificationPanel;
